import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import config from '../config';
import usePersistentState from './usePersistentState';
import { subscribeRealtime } from '../realtime';
import { initNotifSound, playNotifSound } from '../utils/notifSound';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const POLL_MS = 45000;   // FILET DE SÉCURITÉ : si le flux SSE tombe, on rattrape en 45 s
const MAX_ITEMS = 30;

// Notifications par utilisateur (mandat mobile §4) + TEMPS RÉEL (R1).
//
// - TEMPS RÉEL (web) : un flux SSE (GET /api/stream) pousse les événements dès
//   qu'ils surviennent (< 1 s) — la cloche vit en direct. Auth par ticket court
//   (GET /api/stream/ticket) car l'EventSource ne peut pas poser d'en-tête ;
//   reconnexion gérée à la main avec un ticket frais, reprise via after_id.
// - FILET : le sondage GET /api/notifications toutes les 45 s + au retour d'onglet
//   reste actif — si le flux tombe, rien n'est perdu (curseur after_id).
// - Le flux SSE est actif sur le WEB ET sur l'APK (R3) ; l'APK figée actuelle ne
//   le prend qu'à sa prochaine compilation. Le sondage 45 s reste le filet partout.
// - Curseur/vu/poussé persistés par utilisateur ; dédup par id_event.
// - Sur Android (APK) : chaque événement inédit part AUSSI en notification système.
export default function useNotifications({ onNavigate } = {}) {
  const userId = useSelector((s) => s.user?.currentUser?.id);
  const [cursor, setCursor] = usePersistentState(`notif_cursor_${userId || 'anon'}`, 0);
  const [seenId, setSeenId] = usePersistentState(`notif_seen_${userId || 'anon'}`, 0);
  const [pushedId, setPushedId] = usePersistentState(`notif_pushed_${userId || 'anon'}`, 0);
  const [items, setItems] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const busy = useRef(false);
  const amorce = useRef(false);
  const navigateRef = useRef(onNavigate);
  navigateRef.current = onNavigate;

  // Refs de curseur : lues par appliquer() et par l'URL du flux, pour garder
  // appliquer STABLE (sinon le flux SSE se reconnecterait à chaque événement).
  const cursorRef = useRef(cursor); cursorRef.current = cursor;
  const pushedIdRef = useRef(pushedId); pushedIdRef.current = pushedId;

  // Déverrouille l'audio des notifications au 1er geste (politique autoplay).
  useEffect(() => { initNotifSound(); }, []);

  // Permission (Android 13+) + tap sur une notification système → route.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !userId) return undefined;
    LocalNotifications.requestPermissions().catch(() => {});
    const sub = LocalNotifications.addListener('localNotificationActionPerformed', (e) => {
      const route = e?.notification?.extra?.route;
      if (route && navigateRef.current) navigateRef.current(route);
    });
    return () => { sub.then?.((s) => s.remove()).catch?.(() => {}); };
  }, [userId]);

  // Push FCM (natif) : enregistre CE téléphone auprès du serveur pour recevoir
  // les notifications MÊME APP FERMÉE (le SSE/local ne couvre que l'app ouverte).
  // Build sans Firebase (google-services.json absent) : register() échoue →
  // catch silencieux, les notifications locales continuent seules.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !userId) return undefined;
    const subs = [];
    (async () => {
      try {
        // UN CANAL PAR NIVEAU DE CRITICITÉ. Les canaux ciblés par le serveur
        // doivent exister AVANT la première notification, sinon Android la jette.
        //
        // ⚠ Sur Android 8+, les réglages d'un canal (son, importance, vibration)
        // sont FIGÉS à sa création : les modifier dans le code ne change rien
        // sur un téléphone où le canal existe déjà. C'est pourquoi on crée de
        // NOUVEAUX identifiants au lieu de retoucher `ndoe_events` — sans quoi
        // le son resterait celui d'origine sur les téléphones déjà équipés.
        await PushNotifications.createChannel({
          id: 'ndoe_critique',
          name: 'Alertes importantes',
          description: 'Incident de livraison, retard, argent — ce qui demande une réaction',
          importance: 5,        // heads-up + son
          visibility: 1,
          vibration: true,
          sound: 'ndoe_blip',   // res/raw/ndoe_blip.wav, injecté par la CI
        }).catch(() => {});
        await PushNotifications.createChannel({
          id: 'ndoe_activite',
          name: 'Activité courante',
          description: 'Livraisons, commandes, paiements — informatif, sans son',
          importance: 2,        // visible dans le tiroir, ni son ni bandeau
          visibility: 1,
          vibration: false,
        }).catch(() => {});
        // L'ancien canal unique n'a plus d'emploi : le retirer évite qu'il
        // traîne dans les réglages Android du téléphone.
        await PushNotifications.deleteChannel({ id: 'ndoe_events' }).catch(() => {});
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive !== 'granted') return;
        subs.push(await PushNotifications.addListener('registration', async (t) => {
          try {
            // Mémorisé pour le désenregistrement au logout (best-effort).
            localStorage.setItem('push_token', t.value);
            await axios.post(`${DOMAIN}/api/notifications/push-token`, { token: t.value, plateforme: 'android' });
          } catch (e) { /* réessaiera au prochain lancement/login */ }
        }));
        subs.push(await PushNotifications.addListener('pushNotificationActionPerformed', (e) => {
          const route = e?.notification?.data?.route;
          if (route && navigateRef.current) navigateRef.current(route);
        }));
        await PushNotifications.register();
      } catch (e) { /* pas de Firebase dans ce build : rien à faire */ }
    })();
    return () => { subs.forEach((s) => { try { s.remove(); } catch (e) { /* ignore */ } }); };
  }, [userId]);

  const pousserNatif = useCallback(async (nouveaux) => {
    if (!Capacitor.isNativePlatform() || !nouveaux.length) return;
    try {
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') return;
      await LocalNotifications.schedule({
        notifications: nouveaux.slice(0, 5).map((n) => ({
          id: n.id_event,
          title: n.titre,
          body: n.corps || '',
          extra: { route: n.route },
          // Même routage par criticité que le push : sans channelId explicite,
          // Android range la notification locale sur son canal par défaut, qui
          // sonne — l'activité courante redeviendrait bruyante.
          channelId: Number(n.niveau) === 1 ? 'ndoe_critique' : 'ndoe_activite',
        })),
      });
    } catch (e) { /* best-effort : la cloche reste la source fiable */ }
  }, []);

  // Fusionne un lot { last_id, notifications } dans l'état (poll OU flux SSE).
  // Stable (lit les curseurs via refs) : ne provoque pas de reconnexion du flux.
  const appliquer = useCallback((data) => {
    const recus = Array.isArray(data?.notifications) ? data.notifications : [];
    if (data?.prefs) setPrefs(data.prefs);
    if (recus.length) {
      setItems((prev) => {
        const connus = new Set(prev.map((x) => x.id_event));
        const inedits = recus.filter((x) => !connus.has(x.id_event));
        if (!inedits.length) return prev;
        return [...inedits, ...prev].sort((a, b) => b.id_event - a.id_event).slice(0, MAX_ITEMS);
      });
      const seuil = pushedIdRef.current;
      const aPousser = seuil > 0 ? recus.filter((n) => n.id_event > seuil) : [];
      if (aPousser.length) {
        pousserNatif(aPousser);
        // Le son ne se déclenche QUE sur le niveau 1, et c'est lui qui porte
        // désormais la vibration (une vibration à chaque notification faisait
        // partie du bruit). On retient la plus CRITIQUE du lot, pas la plus
        // récente : un incident ne doit pas passer inaperçu parce qu'une
        // notification informative est arrivée juste après. Jamais à
        // l'amorçage (seuil=0 → aPousser vide).
        const plusCritique = aPousser.reduce(
          (a, b) => ((Number(b.niveau) || 2) < (Number(a.niveau) || 2) ? b : a), aPousser[0],
        );
        playNotifSound(plusCritique.niveau);
      }
      setPushedId((v) => Math.max(v, Number(data.last_id) || 0, ...recus.map((n) => n.id_event)));
    }
    if (Number(data?.last_id) > Number(cursorRef.current)) setCursor(Number(data.last_id));
  }, [pousserNatif, setCursor, setPushedId]);

  const charger = useCallback(async () => {
    if (!userId || busy.current || document.visibilityState === 'hidden') return;
    busy.current = true;
    try {
      const incremental = amorce.current && cursorRef.current;
      const p = incremental ? `?after_id=${cursorRef.current}` : '';
      const { data } = await axios.get(`${DOMAIN}/api/notifications${p}`);
      amorce.current = true;
      appliquer(data);
    } catch (e) { /* silencieux : prochaine passe dans 45 s */ }
    finally { busy.current = false; }
  }, [userId, appliquer]);

  // Sondage (amorçage + filet de sécurité).
  useEffect(() => {
    if (!userId) return undefined;
    charger();
    const t = window.setInterval(charger, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') charger(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [userId, charger]);

  // Flux temps réel : la cloche s'abonne au flux SSE PARTAGÉ (une seule connexion
  // pour toute l'app, cf. src/realtime.js). Actif web ET APK (R3). Reprise via le
  // curseur persisté.
  useEffect(() => {
    if (!userId) return undefined;
    return subscribeRealtime(appliquer, { initialCursor: Number(cursorRef.current) || 0 });
  }, [userId, appliquer]);

  const unseen = items.filter((n) => n.id_event > seenId).length;
  const markAllSeen = useCallback(() => {
    setSeenId(items.length ? Math.max(...items.map((n) => n.id_event)) : seenId);
  }, [items, seenId, setSeenId]);

  return { items, unseen, markAllSeen, prefs };
}
